/**
 * ProfileHome — the single-page parent dashboard.
 *
 * Replaces the 5-tab Report/Feed/History/Channels/Settings layout with one
 * scrolling page: weekly verdict + sparkline, "For your review" inbox
 * (corrective actions), this-week stats, flagged videos, top channels,
 * and a 3-card drill-in row to the remaining detail surfaces.
 *
 * Data sources are intentionally pragmatic:
 *  - profile basics + sync status: useGetMyYouTubeProfiles
 *  - sparkline + weekly verdict: useGetHistoryScoresOverTime (dailyScores)
 *  - flagged videos + top channels: useGetLowScoreVideos
 *  - review inbox: useGetCorrectiveActionsForProfile (RECOMMENDED only)
 *
 * AI-authored headlines (StatusCard body, ReviewItem body, FlaggedVideo
 * "why") and per-channel reason text are not yet exposed by the GraphQL
 * layer — those fields ride on `// TODO(ai-summary)` placeholders that
 * derive a flat string from existing data. When the backend grows a
 * proper summarizer, swap the placeholders for the new fields.
 */
import React, {useMemo} from 'react';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {Link, useParams} from 'react-router-dom';
import _ from 'lodash';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import RecommendRoundedIcon from '@mui/icons-material/RecommendRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import ThumbUpRoundedIcon from '@mui/icons-material/ThumbUpRounded';
import ThumbDownRoundedIcon from '@mui/icons-material/ThumbDownRounded';
import {useGetMyYouTubeProfiles} from '../../api/graphql_profiles';
import {useGetHistoryScoresOverTime} from '../../api/graphql_history_score_overtime';
import {useGetLowScoreVideos} from '../../api/graphql_low_score';
import {useGetHighScoreVideos} from '../../api/graphql_high_score';
import {useGetWeeklyBuckets} from '../../api/graphql_weekly_buckets';
import {
  useApplyCorrectiveAction,
  useDismissCorrectiveAction,
  useSkipCorrectiveAction,
} from '../../api/graphql_corrective_actions';
import {
  useReviewQueue,
  useScoreVideo,
} from '../../api/graphql_review_queue';
import {
  ReviewQueueItem,
  ReviewQueueAction,
} from './ReviewQueueItem';
import {
  ChannelCorrectiveAction,
  CorrectiveActionStatus,
  CorrectiveActionType,
  ReviewQueueCard,
  Video,
  VideoCorrectiveAction,
  YouTubeProfile,
} from '../../types/generated/graphql';
import {usePageTitle} from '../../hooks/usePageTitle';
import {Sparkline} from '../YouTube/Sparkline';
import {ProfileSettingsDialog} from './ProfileSettingsDialog';
import {BackLink} from '../Layout/BackLink';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

const DESKTOP_FRESHNESS_MS = 120_000;
type VerdictTone = 'good' | 'attention' | 'warning';

// ---- helpers ---------------------------------------------------------------

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const ms = Date.now() - Date.parse(iso);
  if (Number.isNaN(ms)) return 'never';
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)} min ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)} hr ago`;
  return `${Math.floor(ms / 86_400_000)} day${ms < 2 * 86_400_000 ? '' : 's'} ago`;
}

function isDesktopOnline(profile: YouTubeProfile): boolean {
  if (!profile.desktopLastSeenAt) return false;
  const ms = Date.now() - Date.parse(profile.desktopLastSeenAt);
  return !Number.isNaN(ms) && ms < DESKTOP_FRESHNESS_MS;
}

function isYoutubeConnected(profile: YouTubeProfile): boolean {
  return typeof profile.youtubeSessionValid === 'boolean'
    ? profile.youtubeSessionValid
    : profile.youtubeSignedOut !== true;
}

// Pick a usable thumbnail URL from the YouTube `thumbnails` array.
// Prefers the medium size (index 1, ~320×180) when available — better
// for retina rendering of our ~96×56 cells than the 120×90 default.
function pickThumbUrl(thumbs?: ReadonlyArray<{url?: string | null}> | null): string | null {
  if (!thumbs || thumbs.length === 0) return null;
  return thumbs[1]?.url ?? thumbs[0]?.url ?? null;
}

function formatDuration(seconds?: number | null): string | null {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) return null;
  const s = Math.round(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
  return `${m}:${String(r).padStart(2, '0')}`;
}

function verdictFromAvg(avg: number | null): VerdictTone {
  if (avg == null) return 'attention';
  if (avg >= 7) return 'good';
  if (avg >= 4) return 'attention';
  return 'warning';
}

// Tone-keyed palette for StatusCard / ReviewItem. Chosen to match the
// reference design (emerald / amber / rose) while staying within the
// MUI theme's neutral text colors.
const TONE = {
  good: {
    bg: '#ECFDF5',
    border: '#A7F3D0',
    chipBg: '#10B981',
    accent: '#047857',
    icon: '#10B981',
    spark: '#059669',
  },
  attention: {
    bg: '#FFFBEB',
    border: '#FCD34D',
    chipBg: '#F59E0B',
    accent: '#92400E',
    icon: '#F59E0B',
    spark: '#D97706',
  },
  warning: {
    bg: '#FEF2F2',
    border: '#FCA5A5',
    chipBg: '#EF4444',
    accent: '#991B1B',
    icon: '#EF4444',
    spark: '#DC2626',
  },
} as const;

const SEVERITY_DOT = {
  high: '#EF4444',
  med: '#F59E0B',
  low: '#94A3B8',
} as const;
type Severity = keyof typeof SEVERITY_DOT;
const SEVERITY_LABEL: Record<Severity, {label: string; text: string}> = {
  high: {label: 'High priority', text: '#B91C1C'},
  med: {label: 'Worth a look', text: '#92400E'},
  low: {label: 'Optional', text: '#3730A3'},
};

// Phrase the action as a yes/no question; the verb buttons below
// answer it. "Yes" applies the AI's proposed action ("Block channel
// Kolanii?" → Yes blocks). The branching that used to live on the
// primary button now lives on the title — vocabulary stays Yes/No
// /Skip everywhere.
function questionForType(
  type: CorrectiveActionType,
  channelName?: string | null,
  videoTitle?: string | null,
): string {
  const ch = channelName ?? 'this channel';
  const vid = videoTitle ?? 'this video';
  switch (type) {
    case CorrectiveActionType.BlockChannel:
      return `Block channel: ${ch}?`;
    case CorrectiveActionType.BlockVideo:
      return `Block this video?${vid !== 'this video' ? ` "${vid}"` : ''}`;
    case CorrectiveActionType.FollowChannel:
      return `Follow channel: ${ch}?`;
    case CorrectiveActionType.UnblockChannel:
      return `Allow channel: ${ch}?`;
    case CorrectiveActionType.UnblockVideo:
      return `Allow this video?${vid !== 'this video' ? ` "${vid}"` : ''}`;
    case CorrectiveActionType.UnfollowChannel:
      return `Stop following: ${ch}?`;
    default:
      return 'Apply this suggestion?';
  }
}

// ---- subcomponents ---------------------------------------------------------

function ProfileStrip({
  profile,
  onOpenSettings,
}: {
  profile: YouTubeProfile;
  onOpenSettings: () => void;
}): React.JSX.Element {
  // Worker liveness now spans desktop + mobile + ipad (any device
  // subscribed to the profile that's heartbeating). Falls back to
  // the legacy desktop-only signal when no devices are subscribed
  // yet — keeps the status line working through the rollout window.
  const workers = profile.activeWorkers ?? [];
  const activeWorker = workers.find(w => w.isActive) ?? null;
  const workerActive = activeWorker != null || (workers.length === 0 && isDesktopOnline(profile));
  const youtubeOk = isYoutubeConnected(profile);
  const initial = (profile.name || '?').trim().charAt(0).toUpperCase() || '?';

  const lastSeenIso =
    activeWorker?.lastSeenAt ??
    workers[0]?.lastSeenAt ??
    profile.desktopLastSeenAt ??
    null;
  const viaLabel = activeWorker?.name ?? 'Desktop';
  const statusText =
    youtubeOk && workerActive
      ? `Synced via ${viaLabel} ${formatRelative(lastSeenIso)}`
      : !youtubeOk
        ? "Not collecting — YouTube isn't connected"
        : 'No worker app is running for this profile';
  const statusOk = youtubeOk && workerActive;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        py: 3,
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          flexShrink: 0,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #A5B4FC 0%, #6366F1 100%)',
          color: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 18,
          fontWeight: 700,
          backgroundImage: profile.thumbnails?.[0]?.url
            ? `url(${profile.thumbnails[0].url})`
            : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {!profile.thumbnails?.[0]?.url && initial}
      </Box>
      <Box sx={{flex: 1, minWidth: 0}}>
        <Typography sx={{fontSize: 22, fontWeight: 800, color: '#0F172A', lineHeight: 1.2}}>
          {profile.name || 'Untitled profile'}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{mt: 0.5}}>
          {statusOk ? (
            <CheckCircleRoundedIcon sx={{fontSize: 14, color: '#10B981'}} />
          ) : (
            <ErrorRoundedIcon sx={{fontSize: 14, color: '#F59E0B'}} />
          )}
          <Typography sx={{fontSize: 12, color: '#64748B'}}>{statusText}</Typography>
          {/* The strip shows "Not collecting — YouTube isn't
              connected" as plain text by default. When YouTube is
              the failing leg, append a Fix-it link to /welcome —
              the interactive first-run checklist that walks the
              parent through installing the desktop app and signing
              the kid in. The Desktop-offline case has no
              equivalent affordance: opening the desktop app is the
              fix, and we can't trigger that from a web page. */}
          {!youtubeOk && (
            <Typography
              component={Link}
              to="/welcome"
              sx={{
                fontSize: 12,
                color: '#3448F0',
                fontWeight: 600,
                textDecoration: 'none',
                ml: 0.5,
                '&:hover': {textDecoration: 'underline'},
              }}
            >
              Fix it →
            </Typography>
          )}
        </Stack>
      </Box>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<SettingsRoundedIcon sx={{fontSize: 16}} />}
          onClick={onOpenSettings}
          sx={{
            borderRadius: 999,
            borderColor: '#E2E8F0',
            color: '#334155',
            fontWeight: 600,
            fontSize: 13,
            textTransform: 'none',
            px: 1.75,
            bgcolor: '#FFFFFF',
            '&:hover': {borderColor: '#CBD5E1', bgcolor: '#F8FAFC'},
          }}
        >
          Profile settings
        </Button>
        {/* Admin re-eval moved to the Admin Command Center profile row
            (/admin/operations/profiles → expand profile → Identity card). */}
      </Stack>
    </Box>
  );
}

function StatusCard({
  tone,
  headline,
  body,
  sparkline,
  weekLabel,
}: {
  tone: VerdictTone;
  headline: string;
  body: string;
  sparkline: number[];
  weekLabel: string;
}): React.JSX.Element {
  const palette = TONE[tone];
  const chipLabel =
    tone === 'good' ? 'All good' : tone === 'attention' ? 'Needs your input' : 'Heads up';
  const Icon =
    tone === 'good'
      ? CheckCircleRoundedIcon
      : tone === 'attention'
        ? WarningAmberRoundedIcon
        : ErrorRoundedIcon;
  return (
    <Box
      sx={{
        borderRadius: 3,
        border: `1px solid ${palette.border}`,
        bgcolor: palette.bg,
        p: 3,
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
        <Box sx={{flex: 1, minWidth: 0}}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.25,
                py: 0.25,
                borderRadius: 999,
                bgcolor: palette.chipBg,
                color: '#FFFFFF',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.2,
              }}
            >
              <Icon sx={{fontSize: 13, color: '#FFFFFF'}} />
              {chipLabel}
            </Box>
            <Typography sx={{fontSize: 12, color: palette.accent, fontWeight: 600}}>
              {weekLabel}
            </Typography>
          </Stack>
          <Typography
            sx={{
              mt: 1.5,
              fontSize: 22,
              fontWeight: 800,
              color: '#0F172A',
              lineHeight: 1.25,
            }}
          >
            {headline}
          </Typography>
          <Typography
            sx={{
              mt: 1,
              fontSize: 14,
              color: '#334155',
              lineHeight: 1.55,
              maxWidth: 560,
            }}
          >
            {body}
          </Typography>
        </Box>
        <Box sx={{color: palette.spark, flexShrink: 0, mt: 0.5}}>
          <Sparkline data={sparkline} width={140} height={36} stroke={palette.spark} />
        </Box>
      </Stack>
    </Box>
  );
}

export interface ReviewItemViewModel {
  id: string;
  severity: Severity;
  title: string;
  body: string;
  evidence: string;
  evidenceHref?: string;
  raw: ChannelCorrectiveAction | VideoCorrectiveAction;
  // `imageKind` decides which media to render in the leading slot:
  // `video` → use a 16:9 thumbnail; `channel` → use a round avatar.
  // Channel-level actions (block/follow/allow channel) keep the avatar
  // even if the channel happens to have no resolvable URL — we don't
  // want to mislead with a video thumb for a channel-level decision.
  imageKind: 'video' | 'channel';
  thumbnailUrl: string | null;
  avatarUrl: string | null;
  imageSeed: string;
  imageLabel: string;
}

export function ReviewItemMedia({item}: {item: ReviewItemViewModel}): React.JSX.Element {
  if (item.imageKind === 'video') {
    if (item.thumbnailUrl) {
      return (
        <Box
          sx={{
            position: 'relative',
            width: 96,
            height: 56,
            flexShrink: 0,
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: 'inset 0 0 0 1px #E2E8F0',
            bgcolor: '#F1F5F9',
            mt: 0.25,
          }}
        >
          <Box
            component="img"
            src={item.thumbnailUrl}
            alt=""
            loading="lazy"
            sx={{width: '100%', height: '100%', objectFit: 'cover', display: 'block'}}
          />
        </Box>
      );
    }
    // Fallback: same shape as the live thumbnail, slate placeholder.
    return (
      <Box
        aria-hidden
        sx={{
          width: 96,
          height: 56,
          flexShrink: 0,
          borderRadius: 2,
          bgcolor: '#E2E8F0',
          color: '#64748B',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mt: 0.25,
        }}
      >
        <PlayCircleOutlineRoundedIcon sx={{fontSize: 22}} />
      </Box>
    );
  }

  // imageKind === 'channel'
  if (item.avatarUrl) {
    return (
      <Box
        component="img"
        src={item.avatarUrl}
        alt={item.imageLabel}
        loading="lazy"
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: '50%',
          objectFit: 'cover',
          display: 'block',
          mt: 0.25,
        }}
      />
    );
  }
  const [g0, g1] = gradientFor(item.imageSeed);
  const initial = (item.imageLabel?.[0] ?? '?').toUpperCase();
  return (
    <Box
      aria-hidden
      sx={{
        width: 40,
        height: 40,
        flexShrink: 0,
        borderRadius: '50%',
        background: `linear-gradient(135deg, ${g0}, ${g1})`,
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: 800,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mt: 0.25,
        boxShadow: '0 1px 2px rgba(15,23,42,0.12)',
      }}
    >
      {initial}
    </Box>
  );
}

export function ReviewItem({
  item,
  applying,
  onApply,
  onSkip,
  onDismiss,
}: {
  item: ReviewItemViewModel;
  applying: boolean;
  // Yes — answer the card's question affirmatively. Same as
  // applying the AI's proposed corrective action.
  onApply: (item: ReviewItemViewModel) => void;
  // Skip — "not now, come back later". Server-side snooze (7d).
  onSkip: (item: ReviewItemViewModel) => void;
  // No — answer the card's question negatively. Same as
  // dismissing the suggestion permanently. (Was a separate X
  // affordance before; collapsed into the No verb.)
  onDismiss: (item: ReviewItemViewModel) => void;
}): React.JSX.Element {
  const sev = SEVERITY_LABEL[item.severity];
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        gap: 2,
        borderRadius: 3,
        border: '1px solid #E2E8F0',
        bgcolor: '#FFFFFF',
        p: 2.5,
        transition: 'border-color 120ms',
        '&:hover': {borderColor: '#CBD5E1'},
      }}
    >
      <ReviewItemMedia item={item} />
      <Box sx={{flex: 1, minWidth: 0}}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: SEVERITY_DOT[item.severity],
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: sev.text,
              textTransform: 'uppercase',
              letterSpacing: 0.6,
            }}
          >
            {sev.label}
          </Typography>
        </Stack>
        <Typography sx={{fontSize: 15, fontWeight: 700, color: '#0F172A', mt: 1}}>
          {item.title}
        </Typography>
        <Typography sx={{fontSize: 13, color: '#475569', mt: 0.75, lineHeight: 1.55}}>
          {item.body}
        </Typography>
        <Typography sx={{fontSize: 12, color: '#94A3B8', mt: 1, fontWeight: 500}}>
          {item.evidence}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1} sx={{mt: 1.75}} flexWrap="wrap">
          <Button
            variant="contained"
            size="small"
            disabled={applying}
            onClick={() => onApply(item)}
            startIcon={<ThumbUpRoundedIcon sx={{fontSize: 14}} />}
            sx={{
              borderRadius: 999,
              bgcolor: '#10B981',
              fontSize: 13,
              fontWeight: 700,
              textTransform: 'none',
              px: 2,
              boxShadow: 'none',
              '&:hover': {bgcolor: '#059669', boxShadow: 'none'},
            }}
          >
            Yes
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={applying}
            onClick={() => onDismiss(item)}
            startIcon={<ThumbDownRoundedIcon sx={{fontSize: 14}} />}
            sx={{
              borderRadius: 999,
              borderColor: '#FECACA',
              color: '#B91C1C',
              fontSize: 13,
              fontWeight: 700,
              textTransform: 'none',
              px: 2,
              '&:hover': {borderColor: '#FCA5A5', bgcolor: '#FEF2F2'},
            }}
          >
            No
          </Button>
          <Button
            variant="text"
            size="small"
            disabled={applying}
            onClick={() => onSkip(item)}
            sx={{
              borderRadius: 999,
              color: '#64748B',
              fontSize: 13,
              fontWeight: 600,
              textTransform: 'none',
              px: 1.5,
              '&:hover': {bgcolor: '#F1F5F9'},
            }}
          >
            Skip
          </Button>
          {item.evidenceHref && (
            <Typography
              component={Link}
              to={item.evidenceHref}
              sx={{
                ml: 0.5,
                fontSize: 12,
                color: '#3448F0',
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': {textDecoration: 'underline'},
              }}
            >
              See why ›
            </Typography>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

function BucketRow({
  color,
  label,
  count,
  loading,
}: {
  color: string;
  label: string;
  count: number;
  loading: boolean;
}): React.JSX.Element {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: color}} />
        <Typography sx={{fontSize: 13, color: '#475569'}}>{label}</Typography>
      </Stack>
      <Typography sx={{fontSize: 14, fontWeight: 700, color: '#0F172A'}}>
        {loading ? '—' : count}
      </Typography>
    </Stack>
  );
}

function FlaggedVideo({
  pid,
  video,
  reason,
}: {
  pid: string;
  video: Video;
  reason: string | null;
}): React.JSX.Element {
  const thumbUrl = pickThumbUrl(video.thumbnails);
  const duration = formatDuration(video.duration);
  return (
    <Box
      component={Link}
      to={`/profile/${pid}/video/${video.id}`}
      sx={{
        display: 'flex',
        gap: 1.5,
        textDecoration: 'none',
        color: 'inherit',
        borderRadius: 2,
        border: '1px solid #FECACA',
        bgcolor: '#FEF2F2',
        p: 1.5,
        transition: 'border-color 120ms',
        '&:hover': {borderColor: '#FCA5A5'},
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: 96,
          height: 56,
          flexShrink: 0,
          borderRadius: 2,
          overflow: 'hidden',
          // Rose ring — row-level "flagged" cue. The thumbnail itself is
          // not tinted/blurred; recognition is the whole point.
          boxShadow: 'inset 0 0 0 1px #FECACA',
          bgcolor: thumbUrl ? '#FECACA' : '#FECACA',
          color: '#B91C1C',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {thumbUrl ? (
          <Box
            component="img"
            src={thumbUrl}
            alt=""
            loading="lazy"
            sx={{width: '100%', height: '100%', objectFit: 'cover', display: 'block'}}
          />
        ) : (
          <PlayCircleOutlineRoundedIcon sx={{fontSize: 22}} />
        )}
        {duration && (
          <Box
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              px: 0.5,
              py: 0.125,
              bgcolor: 'rgba(0,0,0,0.45)',
              backdropFilter: 'blur(4px)',
              borderRadius: 999,
              color: '#FFFFFF',
              fontSize: 9,
              fontWeight: 700,
              lineHeight: 1.2,
              letterSpacing: 0.2,
            }}
          >
            {duration}
          </Box>
        )}
      </Box>
      <Box sx={{flex: 1, minWidth: 0}}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: '#0F172A',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {video.title}
        </Typography>
        <Typography sx={{fontSize: 12, color: '#64748B', mt: 0.25}} noWrap>
          {video.author?.name || 'Unknown channel'}
        </Typography>
        {reason && (
          <Typography sx={{fontSize: 12, color: '#475569', mt: 0.5, lineHeight: 1.4}}>
            {reason}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// Stable hash → gradient pair, used as the letter-fallback background
// when a channel has no avatar URL. Keeps the same channel rendering
// the same fallback color across renders / sessions.
const AVATAR_GRADIENTS: ReadonlyArray<readonly [string, string]> = [
  ['#7DD3FC', '#6366F1'],
  ['#FCD34D', '#FB923C'],
  ['#FB7185', '#E11D48'],
  ['#6EE7B7', '#10B981'],
  ['#FBBF24', '#F43F5E'],
  ['#A5B4FC', '#6366F1'],
];

function gradientFor(seed: string): readonly [string, string] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return AVATAR_GRADIENTS[Math.abs(h) % AVATAR_GRADIENTS.length];
}

function ChannelTag({
  id,
  count,
  name,
  tag,
  avatarUrl,
}: {
  id: string;
  count: number;
  name: string;
  tag: 'Trusted' | 'Review' | 'Concern';
  avatarUrl?: string | null;
}): React.JSX.Element {
  const tone =
    tag === 'Trusted'
      ? {bg: '#ECFDF5', fg: '#047857', border: '#A7F3D0'}
      : tag === 'Review'
        ? {bg: '#FFFBEB', fg: '#92400E', border: '#FCD34D'}
        : {bg: '#FEF2F2', fg: '#991B1B', border: '#FCA5A5'};
  const [g0, g1] = gradientFor(id || name);
  const initial = (name?.[0] ?? '?').toUpperCase();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        py: 1,
        px: 1.25,
        borderRadius: 2,
        border: '1px solid #E2E8F0',
        bgcolor: '#FFFFFF',
      }}
    >
      {avatarUrl ? (
        <Box
          component="img"
          src={avatarUrl}
          alt={name}
          loading="lazy"
          sx={{
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: '50%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      ) : (
        <Box
          aria-hidden
          sx={{
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${g0}, ${g1})`,
            color: '#FFFFFF',
            fontSize: 12,
            fontWeight: 800,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 1px 2px rgba(15,23,42,0.12)',
          }}
        >
          {initial}
        </Box>
      )}
      <Typography
        sx={{
          fontSize: 13,
          color: '#0F172A',
          fontWeight: 600,
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {name}
      </Typography>
      <Typography sx={{fontSize: 12, color: '#64748B'}}>{count} videos</Typography>
      <Box
        sx={{
          fontSize: 11,
          fontWeight: 700,
          px: 1,
          py: 0.25,
          borderRadius: 999,
          bgcolor: tone.bg,
          color: tone.fg,
          border: `1px solid ${tone.border}`,
        }}
      >
        {tag}
      </Box>
    </Box>
  );
}

function DrillInCard({
  to,
  icon,
  label,
  caption,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  caption: string;
}): React.JSX.Element {
  return (
    <Box
      component={Link}
      to={to}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        flex: 1,
        textDecoration: 'none',
        color: 'inherit',
        borderRadius: 3,
        border: '1px solid #E2E8F0',
        bgcolor: '#FFFFFF',
        p: 2.5,
        transition: 'border-color 120ms, transform 120ms',
        '&:hover': {borderColor: '#A5B4FC', transform: 'translateY(-1px)'},
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 2,
          bgcolor: '#EEF2FF',
          color: '#3448F0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Typography sx={{fontSize: 14, fontWeight: 700, color: '#0F172A'}}>{label}</Typography>
      <Typography sx={{fontSize: 12, color: '#64748B', lineHeight: 1.5}}>{caption}</Typography>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{mt: 'auto', color: '#3448F0'}}>
        <Typography sx={{fontSize: 12, fontWeight: 600}}>Open</Typography>
        <ArrowForwardRoundedIcon sx={{fontSize: 14}} />
      </Stack>
    </Box>
  );
}

// ---- main page -------------------------------------------------------------

export function buildReviewItem(
  raw: ChannelCorrectiveAction | VideoCorrectiveAction,
  pid: string,
): ReviewItemViewModel {
  const isChannel = (raw as ChannelCorrectiveAction).__typename === 'ChannelCorrectiveAction';
  const channel = isChannel ? (raw as ChannelCorrectiveAction).channel : undefined;
  const channelScore = isChannel ? (raw as ChannelCorrectiveAction).channelScore : undefined;
  const video = !isChannel ? (raw as VideoCorrectiveAction).video : undefined;
  const score = isChannel
    ? channelScore?.score ?? null
    : (raw as VideoCorrectiveAction).videoScore ?? null;

  const severity: Severity =
    score != null && score < 4 ? 'high' : score != null && score < 7 ? 'med' : 'low';

  // Title is the yes/no question the verb buttons answer below
  // it. Falls back to the raw action title only if we don't have
  // enough denormed context to phrase a question.
  const title = questionForType(raw.type, channel?.name ?? video?.author?.name, video?.title);

  // TODO(ai-summary): until the backend exposes a parent-readable
  // explanation, fall back to the action's own description (set by
  // the corrective-actions generator).
  const body =
    raw.description ||
    (channelScore?.explanation ?? `Based on ${channelScore?.videoCount ?? 0} videos this week.`);

  const evidence = isChannel
    ? `${channelScore?.videoCount ?? 0} flagged videos · score ${
        channelScore?.score != null ? channelScore.score.toFixed(1) : '—'
      }/10`
    : `Score ${
        (raw as VideoCorrectiveAction).videoScore != null
          ? (raw as VideoCorrectiveAction).videoScore!.toFixed(1)
          : '—'
      }/10`;

  const evidenceHref = isChannel && channel?.id
    ? `/profile/${pid}/channel/${channel.id}`
    : !isChannel && video?.id
      ? `/profile/${pid}/video/${video.id}`
      : undefined;

  // Imagery is driven by the action's *type*, not its `__typename`.
  // The DB transform routes only BlockChannel rows into the
  // ChannelCorrectiveAction branch; FollowChannel / UnblockChannel /
  // UnfollowChannel are stored as VideoCorrectiveAction with the
  // representative `video.author` standing in for the targeted
  // channel. Without this, every Follow/Unblock-channel card
  // rendered a video thumbnail.
  const isChannelTargetType =
    raw.type === CorrectiveActionType.BlockChannel ||
    raw.type === CorrectiveActionType.FollowChannel ||
    raw.type === CorrectiveActionType.UnblockChannel ||
    raw.type === CorrectiveActionType.UnfollowChannel;
  const imageKind: 'video' | 'channel' = isChannelTargetType ? 'channel' : 'video';
  // `channel` only exists on the true-typed ChannelCorrectiveAction
  // branch; for the rest we reach into video.author which is the
  // actual channel target.
  const channelForImage = isChannel ? channel : video?.author;
  const thumbnailUrl =
    imageKind === 'video' ? pickThumbUrl(video?.thumbnails) : null;
  const avatarUrl =
    imageKind === 'channel' ? pickThumbUrl(channelForImage?.thumbnails) : null;
  const imageSeed = isChannelTargetType
    ? channelForImage?.id ?? raw.id
    : video?.id ?? raw.id;
  const imageLabel = isChannelTargetType
    ? channelForImage?.name ?? 'channel'
    : video?.title ?? 'video';

  return {
    id: raw.id,
    severity,
    title,
    body,
    evidence,
    evidenceHref,
    raw,
    imageKind,
    thumbnailUrl,
    avatarUrl,
    imageSeed,
    imageLabel,
  };
}

interface ChannelTagViewModel {
  id: string;
  name: string;
  count: number;
  tag: 'Trusted' | 'Review' | 'Concern';
  avgScore: number;
  avatarUrl: string | null;
}

function buildTopChannels(
  videos: Video[],
  scoresByVideoId: Map<string, number>,
): ChannelTagViewModel[] {
  const groups = _.groupBy(videos, v => v.author?.id ?? 'unknown');
  return _.chain(Object.entries(groups))
    .map(([authorId, vids]): ChannelTagViewModel | null => {
      const first = vids[0];
      if (!first?.author) return null;
      const scoredVids = vids
        .map(v => scoresByVideoId.get(v.id))
        .filter((s): s is number => typeof s === 'number');
      const avg = scoredVids.length
        ? scoredVids.reduce((a, b) => a + b, 0) / scoredVids.length
        : 5;
      const tag: ChannelTagViewModel['tag'] =
        avg >= 7 ? 'Trusted' : avg >= 4 ? 'Review' : 'Concern';
      return {
        id: authorId,
        name: first.author.name || 'Unknown channel',
        count: vids.length,
        tag,
        avgScore: avg,
        avatarUrl: pickThumbUrl(first.author.thumbnails),
      };
    })
    .compact()
    .orderBy(c => c.avgScore, 'asc') // worst first — concerns rise to the top
    .take(6)
    .value();
}

export default function ProfileHome({client}: Props): React.JSX.Element {
  const {pid} = useParams();
  const [settingsOpen, setSettingsOpen] = React.useState(false);

  const {data: profilesData} = useGetMyYouTubeProfiles(client);
  const profile = useMemo<YouTubeProfile | null>(() => {
    const list = _.compact(profilesData?.getMyYouTubeProfiles ?? []);
    return list.find(p => p.id === pid) ?? null;
  }, [profilesData, pid]);

  usePageTitle(profile?.name || 'Profile');

  const {data: scoreTrend, loading: trendLoading} = useGetHistoryScoresOverTime(
    client,
    pid as string,
  );
  const {data: lowScoreData, loading: lowScoreLoading} = useGetLowScoreVideos(
    client,
    pid as string,
  );
  // Top-channels needs to see *both* ends of the score distribution
  // — without high-score videos, every channel's avg is computed
  // only from low-scoring rows and tags Concern across the board.
  const {data: highScoreData} = useGetHighScoreVideos(client, pid as string);
  const {data: bucketsData, loading: bucketsLoading} = useGetWeeklyBuckets(
    client,
    pid as string,
  );
  const {data: reviewData, loading: reviewLoading, refetch: refetchReview} =
    useReviewQueue(client, pid as string);
  const [applyAction, {loading: applying}] = useApplyCorrectiveAction(client);
  const [dismissAction] = useDismissCorrectiveAction(client);
  const [skipAction] = useSkipCorrectiveAction(client);
  const [scoreVideo] = useScoreVideo(client);

  // ---- derived: sparkline + verdict ----
  const sparkSeries = useMemo<number[]>(() => {
    const daily = scoreTrend?.getHistoryScoresOverTime?.dailyScores ?? [];
    if (!daily.length) return [];
    return _.takeRight(
      _.sortBy(daily, d => d.date).map(d => d.score ?? 0),
      7,
    );
  }, [scoreTrend]);

  const verdictAvg = useMemo(() => {
    if (!sparkSeries.length) return null;
    return sparkSeries.reduce((a, b) => a + b, 0) / sparkSeries.length;
  }, [sparkSeries]);
  const verdict = verdictFromAvg(verdictAvg);

  // ---- derived: flagged videos + score map ----
  // The flagged-this-week panel only cares about low scores —
  // keep this map narrow so the "Flagged" cue stays meaningful.
  const flaggedScoreMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of lowScoreData?.getLowScoreVideos.scores ?? []) {
      if (typeof s.score === 'number') m.set(s.videoId, s.score);
    }
    return m;
  }, [lowScoreData]);

  // Combined score map (low + high) feeds the trust-tag for
  // top channels. Without the high end, every channel's avg
  // would be computed from low scores only and tag Concern.
  const combinedScoreMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of lowScoreData?.getLowScoreVideos.scores ?? []) {
      if (typeof s.score === 'number') m.set(s.videoId, s.score);
    }
    for (const s of highScoreData?.getHighScoreVideos.scores ?? []) {
      if (typeof s.score === 'number') m.set(s.videoId, s.score);
    }
    return m;
  }, [lowScoreData, highScoreData]);

  const flaggedVideos = useMemo(() => {
    const vids = lowScoreData?.getLowScoreVideos.videos ?? [];
    return vids.slice(0, 3);
  }, [lowScoreData]);

  const topChannels = useMemo(() => {
    // Union of high + low videos so a channel that only scores
    // well still appears here (and tags Trusted).
    const lowVids = lowScoreData?.getLowScoreVideos.videos ?? [];
    const highVids = highScoreData?.getHighScoreVideos.videos ?? [];
    const merged = _.uniqBy([...lowVids, ...highVids], v => v.id);
    return buildTopChannels(merged, combinedScoreMap);
  }, [lowScoreData, highScoreData, combinedScoreMap]);

  // ---- derived: review inbox ----
  // Server-driven hide: confirm cards whose `skipUntil` is still in
  // the future are filtered out here. `skipUntil` is set by the
  // skipCorrectiveAction mutation and persists across reloads; the
  // card resurfaces automatically once the timestamp passes. Score
  // cards never carry skipUntil (no snooze concept on parent votes).
  const now = Date.now();
  const pendingCards = useMemo<ReviewQueueCard[]>(() => {
    const all = reviewData?.reviewQueue?.cards ?? [];
    return all.filter(c => {
      if (c.kind === 'confirm' && c.skipUntil != null && c.skipUntil > now) {
        return false;
      }
      return true;
    });
  }, [reviewData, now]);

  const totalPending = pendingCards.length;
  // Home-page preview: show 5 cards inline, link to /review for the
  // full deck. Was 3. With SCORE_CARD_CAP bumped to 20 server-side,
  // showing 5 on home strikes the balance between glanceability and
  // letting an engaged parent rip through the easy ones without
  // navigating away.
  const reviewCards = useMemo(() => pendingCards.slice(0, 5), [pendingCards]);

  const handleReviewAction = async (
    card: ReviewQueueCard,
    action: ReviewQueueAction,
  ) => {
    try {
      if (action === 'apply' && card.actionId) {
        await applyAction({variables: {input: {actionId: card.actionId}}});
      } else if (action === 'dismiss' && card.actionId) {
        await dismissAction({variables: {actionId: card.actionId}});
      } else if (action === 'skip' && card.actionId) {
        await skipAction({variables: {actionId: card.actionId, skipDays: 7}});
      } else if (
        (action === 'score_yes' ||
          action === 'score_no' ||
          action === 'score_skip') &&
        card.videoId
      ) {
        const verdict =
          action === 'score_yes'
            ? 'good_fit'
            : action === 'score_no'
              ? 'bad_fit'
              : 'skip';
        await scoreVideo({
          variables: {profileId: pid as string, videoId: card.videoId, verdict},
        });
      }
      await refetchReview();
    } catch (err) {
      console.error(`Failed to ${action} card ${card.id}:`, err);
    }
  };

  // ---- derived: status banner copy from real bucket counts ----
  const buckets = bucketsData?.getWeeklyBuckets;
  const watchedCount = buckets?.watched ?? 0;
  const greatFitCount = buckets?.greatFit ?? 0;
  const borderlineCount = buckets?.borderline ?? 0;
  const flaggedCount = buckets?.flagged ?? 0;
  const kidName = profile?.name?.trim().split(/\s+/)[0] || 'Your kid';

  // Re-derive the verdict from the real bucket counts when we have
  // them — that's the source of truth, sparkline avg is a fallback.
  const bucketVerdict: VerdictTone = useMemo(() => {
    if (!buckets || watchedCount === 0) return verdict;
    const flaggedShare = flaggedCount / watchedCount;
    const goodShare = greatFitCount / watchedCount;
    if (flaggedCount === 0 && goodShare >= 0.6) return 'good';
    if (flaggedShare >= 0.15) return 'warning';
    return 'attention';
  }, [buckets, watchedCount, flaggedCount, greatFitCount, verdict]);

  const headline = useMemo(() => {
    if (bucketVerdict === 'good') return `${kidName} had a great week.`;
    if (bucketVerdict === 'warning') return `${kidName}'s week needs attention.`;
    return 'A few decisions need your input this week.';
  }, [bucketVerdict, kidName]);

  const body = useMemo(() => {
    if (!buckets || watchedCount === 0) {
      return bucketsLoading
        ? `Loading ${kidName}'s week…`
        : `No videos watched this week. ${kidName}'s feed will populate as they watch.`;
    }
    return (
      `${kidName} watched ${watchedCount} video${watchedCount === 1 ? '' : 's'} this week. ` +
      `${greatFitCount} ${greatFitCount === 1 ? 'was' : 'were'} a great fit, ` +
      `${borderlineCount} ${borderlineCount === 1 ? 'was' : 'were'} borderline, and ` +
      `${flaggedCount} I'd flag for you.`
    );
  }, [buckets, watchedCount, greatFitCount, borderlineCount, flaggedCount, bucketsLoading, kidName]);

  if (!pid) return <Container sx={{py: 6}}>Missing profile id.</Container>;

  return (
    <Box sx={{minHeight: '100vh', bgcolor: '#F8FAFC'}}>
      <Container maxWidth="md" sx={{py: 0}}>
        {/* Back affordance — lowercase "all profiles" matches the
            "back to {name}" breadcrumb the layout renders on every
            non-home profile route, so the chrome reads identically
            whether the parent is on the home or deeper. */}
        <Box sx={{pt: 2}}>
          <BackLink to="/profile" label="all profiles" />
        </Box>

        {profile ? (
          <ProfileStrip
            profile={profile}
            onOpenSettings={() => setSettingsOpen(true)}
          />
        ) : (
          <Box sx={{py: 3}}>
            <Skeleton variant="text" width="40%" height={28} />
            <Skeleton variant="text" width="60%" height={16} />
          </Box>
        )}

        {trendLoading && !scoreTrend ? (
          <Box sx={{borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF', p: 3}}>
            <LinearProgress />
          </Box>
        ) : (
          <StatusCard
            tone={bucketVerdict}
            headline={headline}
            body={body}
            sparkline={sparkSeries}
            weekLabel="Last 7 days"
          />
        )}

        {/* For your review — capped at 3, with see-all footer */}
        <Box sx={{mt: 4}}>
          <Stack direction="row" alignItems="flex-end" justifyContent="space-between" sx={{mb: 1.5}}>
            <Box>
              <Typography sx={{fontSize: 18, fontWeight: 800, color: '#0F172A'}}>
                For your review
              </Typography>
              <Typography sx={{fontSize: 13, color: '#64748B', mt: 0.25}}>
                GoodShowHQ AI's top suggestions. Tap Yes, No, or Skip.
              </Typography>
            </Box>
            {totalPending > 0 && (
              <Typography sx={{fontSize: 12, color: '#64748B', fontWeight: 600}}>
                Showing {Math.min(reviewCards.length, totalPending)} of {totalPending}
              </Typography>
            )}
          </Stack>
          {reviewLoading && reviewCards.length === 0 ? (
            <Skeleton variant="rounded" width="100%" height={140} />
          ) : reviewCards.length === 0 ? (
            <Box
              sx={{
                borderRadius: 3,
                border: '1px solid #E2E8F0',
                bgcolor: '#FFFFFF',
                p: 3,
                textAlign: 'center',
              }}
            >
              <Typography sx={{fontSize: 13, color: '#64748B'}}>
                Nothing needs review right now.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {reviewCards.map(card => (
                <ReviewQueueItem
                  key={card.id}
                  card={card}
                  pid={pid as string}
                  busy={applying}
                  onAction={handleReviewAction}
                />
              ))}
            </Stack>
          )}
          {totalPending > reviewCards.length && (
            <Box sx={{mt: 2, textAlign: 'center'}}>
              <Button
                component={Link}
                to={`/profile/${pid}/review`}
                variant="outlined"
                endIcon={<ArrowForwardRoundedIcon sx={{fontSize: 14}} />}
                sx={{
                  borderRadius: 999,
                  borderColor: '#E2E8F0',
                  color: '#334155',
                  fontSize: 13,
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 2,
                  bgcolor: '#FFFFFF',
                  '&:hover': {borderColor: '#CBD5E1', bgcolor: '#F8FAFC'},
                }}
              >
                See all {totalPending} suggestions
              </Button>
            </Box>
          )}
        </Box>

        {/* This week + Flagged videos — 2-col grid. `alignItems:
            flex-start` keeps the cards top-aligned instead of
            stretching the shorter one to match the taller (which
            left dead space under "See full history →"). */}
        <Box
          sx={{
            mt: 4,
            display: 'grid',
            gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'},
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <Card sx={{borderRadius: 3, border: '1px solid #E2E8F0'}} elevation={0}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <HistoryRoundedIcon sx={{fontSize: 16, color: '#94A3B8'}} />
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#64748B',
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                  }}
                >
                  This week
                </Typography>
              </Stack>
              <Typography
                sx={{mt: 1.5, fontSize: 32, fontWeight: 800, color: '#0F172A', lineHeight: 1}}
              >
                {bucketsLoading && !buckets ? '—' : watchedCount}
              </Typography>
              <Typography sx={{mt: 0.5, fontSize: 13, color: '#64748B'}}>videos watched</Typography>

              <Stack spacing={1} sx={{mt: 2.5}}>
                <BucketRow color="#34D399" label="Great fit" count={greatFitCount} loading={bucketsLoading && !buckets} />
                <BucketRow color="#FBBF24" label="Borderline" count={borderlineCount} loading={bucketsLoading && !buckets} />
                <BucketRow color="#FB7185" label="Flagged" count={flaggedCount} loading={bucketsLoading && !buckets} />
              </Stack>

              <Box sx={{mt: 2.5}}>
                <Typography
                  component={Link}
                  to={`/profile/${pid}/history`}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: 13,
                    color: '#3448F0',
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': {textDecoration: 'underline'},
                  }}
                >
                  See full history
                  <ArrowForwardRoundedIcon sx={{fontSize: 14}} />
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{borderRadius: 3, border: '1px solid #E2E8F0'}} elevation={0}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <WarningAmberRoundedIcon sx={{fontSize: 16, color: '#94A3B8'}} />
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#64748B',
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                  }}
                >
                  Flagged this week
                </Typography>
              </Stack>
              <Stack spacing={1} sx={{mt: 1.5}}>
                {lowScoreLoading && flaggedVideos.length === 0 ? (
                  <Skeleton variant="rounded" height={48} />
                ) : flaggedVideos.length === 0 ? (
                  <Typography sx={{fontSize: 12, color: '#64748B'}}>
                    No flagged videos this week.
                  </Typography>
                ) : (
                  flaggedVideos.map(v => (
                    // No per-video parent-readable reason exists today —
                    // `score.explanation` is admin-language ("Aggregate
                    // score X.X based on N agent evaluations, B/N/H
                    // ratings…"), and corrective actions only cover a
                    // subset of flagged videos. Render title + channel
                    // only; revisit when the agent layer ships a
                    // parent-facing summary field.
                    <FlaggedVideo
                      key={v.id}
                      pid={pid}
                      video={v as Video}
                      reason={null}
                    />
                  ))
                )}
              </Stack>
              {flaggedCount > flaggedVideos.length && (
                <Box sx={{mt: 2}}>
                  <Typography
                    component={Link}
                    to={`/profile/${pid}/history`}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontSize: 13,
                      color: '#3448F0',
                      fontWeight: 600,
                      textDecoration: 'none',
                      '&:hover': {textDecoration: 'underline'},
                    }}
                  >
                    See all flagged
                    <ArrowForwardRoundedIcon sx={{fontSize: 14}} />
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Top channels */}
        <Box sx={{mt: 4}}>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{mb: 2}}>
            <Typography sx={{fontSize: 16, fontWeight: 800, color: '#0F172A'}}>
              Top channels this week
            </Typography>
            <Typography
              component={Link}
              to={`/profile/${pid}/channels`}
              sx={{
                fontSize: 12,
                color: '#3448F0',
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': {textDecoration: 'underline'},
              }}
            >
              All channels ↗
            </Typography>
          </Stack>
          {lowScoreLoading && topChannels.length === 0 ? (
            <Skeleton variant="rounded" width="100%" height={140} />
          ) : topChannels.length === 0 ? (
            <Box
              sx={{
                borderRadius: 3,
                border: '1px solid #E2E8F0',
                bgcolor: '#FFFFFF',
                p: 3,
                textAlign: 'center',
              }}
            >
              <Typography sx={{fontSize: 13, color: '#64748B'}}>
                No channel data yet.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {topChannels.map(c => (
                <ChannelTag
                  key={c.id}
                  id={c.id}
                  count={c.count}
                  name={c.name}
                  tag={c.tag}
                  avatarUrl={c.avatarUrl}
                />
              ))}
            </Stack>
          )}
        </Box>

        {/* Drill-ins — split into two labeled rows. Per-kid
            framing on both headers reminds the parent the data is
            per-kid even at section boundaries (the alternative —
            "Your activity / System data" — reads like the surfaces
            could be account-wide). */}
        <Box sx={{mt: 4}}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: '#64748B',
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              mb: 1.5,
            }}
          >
            What you've done for {kidName}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {xs: '1fr', md: 'repeat(2, 1fr)'},
              gap: 2,
            }}
          >
            <DrillInCard
              to={`/profile/${pid}/feedback`}
              icon={<RateReviewRoundedIcon sx={{fontSize: 18}} />}
              label="Your feedback"
              caption="Every Yes / No / Skip you've voted on a video."
            />
            <DrillInCard
              to={`/profile/${pid}/activity/youtube`}
              icon={<TaskAltRoundedIcon sx={{fontSize: 18}} />}
              label="Actions taken"
              caption="Channel blocks, follows, and allows that have run on YouTube."
            />
          </Box>
        </Box>

        <Box sx={{mt: 4, mb: 6}}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: '#64748B',
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              mb: 1.5,
            }}
          >
            GoodShowHQ's data for {kidName}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {xs: '1fr', sm: 'repeat(3, 1fr)'},
              gap: 2,
            }}
          >
            <DrillInCard
              to={`/profile/${pid}/history`}
              icon={<HistoryRoundedIcon sx={{fontSize: 18}} />}
              label="Full watch history"
              caption="Every video they watched, newest first."
            />
            <DrillInCard
              to={`/profile/${pid}/feed`}
              icon={<RecommendRoundedIcon sx={{fontSize: 18}} />}
              label="All recommendations"
              caption="What YouTube suggested in their feed."
            />
            <DrillInCard
              to={`/profile/${pid}/channels`}
              icon={<GroupsRoundedIcon sx={{fontSize: 18}} />}
              label="All channels"
              caption="Every channel that's shown up this week."
            />
          </Box>
        </Box>

        {/* Connected devices — phones / laptops subscribed to this
            profile that fetch its data + run scoring tasks in the
            background. Lives at the bottom of the page because it's
            infrastructure detail; parents care about it occasionally
            (debugging "why isn't my feed updating?") rather than as
            part of the daily review surface. Hidden when no device
            has subscribed yet. */}
        {profile && (
          <ProfileWorkersPanel
            workers={(profile.activeWorkers ?? []) as ActiveWorkerVM[]}
          />
        )}

        {/* Mobile cross-promo. Always shown for now; gate on
            "registered mobile device" once that flag exists on the
            profile/account schema. */}
        <Box sx={{textAlign: 'center', pb: 6}}>
          <Typography sx={{fontSize: 12, color: '#94A3B8'}}>
            Faster on mobile —{' '}
            <Box
              component="a"
              href="https://goodshowhq.app/get"
              target="_blank"
              rel="noopener"
              sx={{
                color: '#3448F0',
                fontWeight: 600,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                '&:hover': {textDecoration: 'underline'},
              }}
            >
              get the app for swipe-to-review
              <OpenInNewRoundedIcon sx={{fontSize: 12}} />
            </Box>
          </Typography>
        </Box>
      </Container>

      <ProfileSettingsDialog
        client={client}
        profileId={settingsOpen ? pid : null}
        profileName={profile?.name}
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </Box>
  );
}

interface ActiveWorkerVM {
  deviceId: string;
  name: string;
  type: string;
  lastSeenAt: string;
  isActive: boolean;
}

// Per-profile worker list. Replaces the desktop-only "Desktop agent
// isn't running" framing — any subscribed device shows up here with
// a green/amber dot reflecting whether its last heartbeat is fresh.
function ProfileWorkersPanel({
  workers,
}: {
  workers: ActiveWorkerVM[];
}): React.JSX.Element | null {
  if (!workers || workers.length === 0) return null;
  return (
    <Box sx={{mt: 4}}>
      <Stack direction="row" alignItems="flex-end" justifyContent="space-between" sx={{mb: 1.5}}>
        <Box>
          <Typography sx={{fontSize: 18, fontWeight: 800, color: '#0F172A'}}>
            Connected devices
          </Typography>
          <Typography sx={{fontSize: 13, color: '#64748B', mt: 0.25}}>
            Phones and laptops linked to this profile that fetch its data and run scoring in the background.
          </Typography>
        </Box>
        <Typography sx={{fontSize: 12, color: '#64748B', fontWeight: 600}}>
          {workers.filter(w => w.isActive).length} active · {workers.length} total
        </Typography>
      </Stack>
      <Stack spacing={1}>
        {workers.map(w => (
          <Box
            key={w.deviceId}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderRadius: 3,
              border: '1px solid #E2E8F0',
              bgcolor: '#FFFFFF',
              px: 2.5,
              py: 1.5,
            }}
          >
            <Box>
              <Typography sx={{fontSize: 14, fontWeight: 700, color: '#0F172A'}}>
                {w.name}
              </Typography>
              <Typography sx={{fontSize: 12, color: '#64748B', mt: 0.25}}>
                {w.type === 'mobile' ? 'Mobile' : 'Desktop'} ·{' '}
                {w.isActive
                  ? `active ${formatRelative(w.lastSeenAt)}`
                  : `last seen ${formatRelative(w.lastSeenAt)}`}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: w.isActive ? '#10B981' : '#CBD5E1',
                flexShrink: 0,
              }}
            />
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
